import {
  Button,
  FDropdown,
  MAddOrderConfirmation,
  Textarea,
  WayConnection,
  YearDropdown,
} from '../../../../components';
import { toast } from 'react-toastify';
import { userCar, userMail, LetterIcon } from '../../../../assets/icons';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../languages/i18n';
import Counter from '../../../../components/inputs/Counter';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import {
  addOffer,
  getAllCarBrands,
  getColors,
  getCities,
  getModelsOfBrand,
  editOrder,
} from '../../../../services/apis/offer';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import useAddOrderSchema from '../../../../schema/order';

const Add = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { orderId } = useParams();
  const location = useLocation();
  const isEditMode = Boolean(orderId);
  const isArabic = i18n.language === 'ar';
  const [openModal, setOpenModal] = useState(false);
  const [brands, setBrands] = useState([]);
  const [models, setModels] = useState([]);
  const [groupedModels, setGroupedModels] = useState([]);
  const [cities, setCities] = useState([]);
  const [colors, setColors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCommunicationOptions, setSelectedCommunicationOptions] =
    useState([1]);
  const [isPriceNotDetermined, setIsPriceNotDetermined] = useState(false);
  const yearOptions = Array.from({ length: 25 }, (_, index) => 2000 + index);
  const addOrderSchema = useAddOrderSchema();
  const { state } = location;
  const existingOrder = state?.order;
  const closeModal = () => {
    setOpenModal(false);
  };

  const handleCommunicationOptionsSelect = (updatedOptions) => {
    setSelectedCommunicationOptions(updatedOptions);
  };

  const handleCheckBoxChange = () => {
    setIsPriceNotDetermined(!isPriceNotDetermined);
  };

  const {
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(addOrderSchema),
    defaultValues: {
      requestTitle: '',
      description: '',
      yearFrom: undefined,
      yearTo: undefined,
      priceFrom: undefined,
      priceTo: undefined,
      odoFrom: undefined,
      odoTo: undefined,
      cars: [
        {
          brandId: 0,
          models: [],
        },
      ],
      cityIds: [],
      colorIds: [],
      communicationOptionIds: [],
      phoneNumber: '',
      whatsappNumber: '',
    },
  });

  const watchBrand = watch('carBrands');

  const handleAddOfferSubmit = async (data) => {
    try {
      // Check description length first
      if (data.description && data.description.length > 2000) {
        toast.error(
          t('addOffer.descriptionTooLong') ||
            'Description cannot exceed 2000 characters'
        );
        return;
      }

      // Check description length first
      if (data.description && data.description.length > 2000) {
        toast.error(
          t('addOffer.descriptionTooLong') ||
            'Description cannot exceed 2000 characters'
        );
        return;
      }

      // Validate that carBrands and carType have corresponding entries
      if (data?.carBrands?.length === 0) {
        toast.error(t('addOffer.brandSelect'));
        return;
      }

      // Check if each brand has at least one model selected
      const selectedModels = data.carType || [];
      const brandsWithoutModels = data.carBrands.filter((brandId) => {
        return !selectedModels.some((modelId) => {
          const modelGroup = groupedModels.find((group) =>
            group.models.some((model) => model.value === modelId)
          );
          return modelGroup && modelGroup.brandId === brandId;
        });
      });

      if (brandsWithoutModels.length > 0) {
        toast.error(t('addOffer.modelSelect'));
        return;
      }
      // Validate year range
      if (
        data.yearTo &&
        data.yearFrom &&
        Number(data.yearTo) < Number(data.yearFrom)
      ) {
        toast.error(
          t('addOffer.invalidYearRange') || 'Error: Invalid year range.'
        );
        return;
      }

      // Validate odometer range only if both values are not zero
      if (
        !(Number(data.kilometersTo) === 0 && Number(data.kilometersFrom) === 0) &&
        Number(data.kilometersTo) < Number(data.kilometersFrom)
      ) {
        toast.error(
          t('addOffer.invalidOdometerRange') || 'Error: Invalid odometer range.'
        );
        return;
      }

      // Validate price range
      if (!isPriceNotDetermined) {
        if (data.priceTo === undefined) {
          toast.error(t('addOffer.priceRequired'));
          return;
        }
        if (Number(data.priceTo) < 0 || Number(data.priceFrom) < 0) {
          toast.error(
            t('addOffer.priceRangeInvalid') || 'Error: Invalid price range.'
          );
          return;
        }
        if (Number(data.priceTo) < Number(data.priceFrom)) {
          toast.error(
            t('addOffer.invalidPriceRange') || 'Error: To price must be greater than From price.'
          );
          return;
        }
      }
      const payload = {
        requestTitle: data.requestTitle || '',
        description: data.description || '',
        yearFrom: Number(data.yearFrom) || 0,
        yearTo: Number(data.yearTo) || 0,
        priceFrom: Number(data.priceFrom) || 0,
        priceTo: Number(data.priceTo) || 0,
        odoFrom: Number(data.kilometersFrom) || 0,
        odoTo: Number(data.kilometersTo) || 0,
        cars: data.carBrands.map((brandId) => {
          // Get all models for this brand
          const brandModels = data.carType.filter(modelId => {
            const modelGroup = groupedModels.find(group => 
              group.models.some(model => model.value === modelId)
            );
            return modelGroup && modelGroup.brandId === brandId;
          });
          
          return {
            brandId: brandId,
            models: brandModels,
          };
        }),
        cityIds: data.city || [],
        colorIds: data.color || [],
        communicationOptionIds: selectedCommunicationOptions,
        phoneNumber: data.phoneNumber || '',
        whatsappNumber: data.whatsappNumber || '',
      };
      setLoading(true);
      if (isEditMode && orderId) {
        try {
          // Ensure orderId is a valid string
          const validOrderId = String(orderId).trim();

          if (!validOrderId) {
            throw new Error('Invalid order ID');
          }

          const response = await editOrder(validOrderId, payload);
          if (response?.data) {
            setOpenModal(true);
            setTimeout(() => navigate(`/orders/${validOrderId}`), 3000);
          } else {
            throw new Error('Invalid response from server');
          }
        } catch (error) {
          console.error('Error editing order:', error);
          setLoading(false);
          // Show error to user
          alert(error.message || 'Error updating order');
        } finally {
          setLoading(false);
        }
      } else {
        try {
          const response = await addOffer(payload);
          if (response?.data?.id) {
            setOpenModal(true);
            setTimeout(() => {
              navigate('/orders');
            }, 3000);
          }
        } catch (error) {
          console.error('Error adding offer:', error);
        } finally {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Error submitting offer:', error);
    }
  };

  useEffect(() => {
    // Fetch all brands, colors, and cities on component load
    const fetchInitialData = async () => {
      const brandData = await getAllCarBrands();
      const colorData = await getColors();
      const cityData = await getCities();

      if (brandData) {
        const formattedBrands = brandData.map((brand) => ({
          value: brand.id,
          label: isArabic ? brand.nameAr : brand.nameEn,
          icon: brand.image,
        }));
        setBrands(formattedBrands);
      }

      if (colorData) {
        const formattedColors = colorData?.data?.items?.map((color) => ({
          value: color.id,
          label: isArabic ? color.nameAr : color.nameEn,
          isAnyColor: color.isAnyColor || false
        }));
        // Sort to put "any color" first
        const sortedColors = formattedColors.sort((a, b) => {
          if (a.isAnyColor) return -1;
          if (b.isAnyColor) return 1;
          return 0;
        });
        setColors(sortedColors);
      }

      if (cityData) {
        const formattedCities = cityData.map((city) => ({
          value: city.id,
          label: isArabic ? city.nameAr : city.nameEn,
        }));
        setCities(formattedCities);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchModelsForBrands = async () => {
      if (watchBrand?.length > 0) {
        try {
          const modelDataList = await Promise.all(
            watchBrand.map((brandId) => getModelsOfBrand(brandId))
          );

          const grouped = watchBrand.map((brandId, index) => {
            const brand = brands.find((b) => b.value === brandId);
            const models = modelDataList[index].map((model) => ({
              value: model.id,
              label: isArabic ? model.nameAr : model.nameEn,
            }));
            return {
              brandId,
              brandLabel: brand?.label,
              models,
            };
          });

          setGroupedModels(grouped);
          
          // Get current selected models
          const currentModels = watch('carType') || [];
          
          // Filter out models that don't belong to current brands
          const validModels = currentModels.filter(modelId => {
            return grouped.some(group => 
              group.models.some(model => model.value === modelId)
            );
          });
          
          // Update the form with only valid models
          setValue('carType', validModels);
          
        } catch (error) {
          console.error('Error fetching models:', error);
        }
      } else {
        setGroupedModels([]);
        setValue('carType', []);
      }
    };

    fetchModelsForBrands();
  }, [watchBrand, brands]);

  useEffect(() => {
    if (isEditMode && existingOrder) {
      // Check if price is not determined (both priceFrom and priceTo are 0)
      const isPriceUndetermined =
        existingOrder.priceFrom === 0 && existingOrder.priceTo === 0;
      setIsPriceNotDetermined(isPriceUndetermined);

      // Format the data as per form structure
      const formattedData = {
        requestTitle: existingOrder.requestTitle || '',
        description: existingOrder.description || '',
        yearFrom: existingOrder.yearFrom || 0,
        yearTo: existingOrder.yearTo || 0,
        priceFrom: isPriceUndetermined ? 0 : existingOrder.priceFrom || 0,
        priceTo: isPriceUndetermined ? 0 : existingOrder.priceTo || 0,
        odoFrom: existingOrder.odoFrom || 0,
        odoTo: existingOrder.odoTo,
        carBrands: existingOrder.cars.map((car) => car.id), // Get all brand IDs
        carType: existingOrder.cars.flatMap((car) => 
          car.models.map(model => model.id) // Get all model IDs from each car
        ),
        city: existingOrder.cities.map((city) => city.id) || [], // Extract city IDs
        color: existingOrder.colors.map((color) => color.id) || [], // Extract color IDs
        communicationOptionIds:
          existingOrder.communicationOptions.map((option) => option.id) || [], // Extract communication option IDs
        phoneNumber: existingOrder.phoneNumber || '',
        whatsappNumber: existingOrder.whatsappNumber || '',
      };
      reset(formattedData); // Reset the form with the formatted data
    }
  }, [isEditMode, existingOrder, reset]);

  return (
    <div className="bg-gradient-to-b from-[#FFFFFF] to-[#D7E2FF] p-10">
      <div className="w-[90%] mr-auto ml-auto">
        <div className="text-[17px] text-[#000000] font-500 text-center mb-10">
          {isEditMode ? t('myOrders.editOrder') : t('myOrders.orderCar')}
        </div>

        <form onSubmit={handleSubmit(handleAddOfferSubmit)}>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-2 xl:grid-cols-2 sm:grid-cols-1 gap-x-4 mb-4 gap-4">
            <div className="display-flex flex-col">
              <FDropdown
                control={control}
                name="carBrands"
                label={t('myOrders.brandCar')}
                placeholder={t('myOrders.selectBrandCar')}
                icon={userMail}
                options={brands}
              />
              <div className="mb-4">
                {errors.carBrands && (
                  <span className="text-red-500 text-sm block mt-1">
                    {t('validation.requiredCarBrand')}
                  </span>
                )}
              </div>
            </div>

            <div className="display-flex flex-col">
              <FDropdown
                control={control}
                name="carType"
                label={t('myOrders.carType')}
                placeholder={t('myOrders.selectCarType')}
                icon={LetterIcon}
                options={models}
                groupedOptions={groupedModels}
                disabled={watchBrand?.length === 0}
              />
              <div className="mb-4">
                {errors.carType && (
                  <span className="text-red-500 text-sm block mt-1">
                    {t('validation.requiredCarModel')}
                  </span>
                )}
              </div>
            </div>

            <FDropdown
              control={control}
              name="city"
              label={t('myOrders.city')}
              placeholder={t('myOrders.selectCity')}
              icon={userMail}
              options={cities}
            />

            <FDropdown
              control={control}
              name="color"
              icon={LetterIcon}
              options={colors}
              label={t('myOrders.color')}
              placeholder={t('myOrders.selectColor')}
            />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-2 xl:grid-cols-2 sm:grid-cols-1 gap-x-4 mt-4items-center">
            <div className="grid md:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-2 xl:grid-cols-2 sm:grid-cols-1 gap-x-4">
              <div>
                <YearDropdown
                  control={control}
                  name="yearFrom"
                  icon={userMail}
                  label={t('myOrders.year')}
                  placeholder={t('myOrders.from')}
                  options={yearOptions.map((year) => ({
                    value: year,
                    label: year,
                  }))}
                />
                <div className="mb-4">
                  {errors.yearFrom && (
                    <span className="text-red-500 text-sm block mt-1">
                      {errors.yearFrom.message}
                    </span>
                  )}
                </div>
              </div>

              <div className="mt-5">
                <YearDropdown
                  control={control}
                  name="yearTo"
                  icon={''}
                  label={''}
                  placeholder={t('myOrders.to')}
                  options={yearOptions.map((year) => ({
                    value: year,
                    label: year,
                  }))}
                />
                <div className="mb-4">
                  {errors.yearTo && (
                    <span className="text-red-500 text-sm block mt-1">
                      {errors.yearTo.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-2 xl:grid-cols-2 sm:grid-cols-1 gap-x-4">
              <Controller
                name="kilometersFrom"
                defaultValue={existingOrder?.odoFrom || 0}
                control={control}
                render={({ field }) => (
                  <Counter
                    {...field}
                    initialValue={existingOrder?.odoFrom}
                    icon={LetterIcon}
                    label={t('myOrders.kilometers')}
                    placeholder={t('myOrders.from')}
                    step={10000}
                  />
                )}
              />
              <Controller
                name="kilometersTo"
                defaultValue={existingOrder?.odoTo || 0}
                control={control}
                render={({ field }) => (
                  <Counter
                    {...field}
                    initialValue={existingOrder?.odoTo}
                    icon={''}
                    label={t('myOrders.to')}
                    placeholder={t('myOrders.to')}
                    className="mt-2"
                    step={10000}
                  />
                )}
              />
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-2 xl:grid-cols-2 sm:grid-cols-1 gap-x-4 mt-5">
            <div className="flex flex-col">
              <div className="flex flex-row items-center">
                <input
                  type="checkbox"
                  checked={isPriceNotDetermined}
                  onChange={handleCheckBoxChange}
                  className="appearance-none w-5 h-5 rounded-[5px] border border-[#3E0292] checked:bg-[#3E0292] checked:border-transparent focus:outline-none"
                />
                <div className="text-[#303030] text-[13px] font-500 mx-5">
                  {t('myOrders.priceNotDetermined')}
                </div>
              </div>

              {/* Conditionally render the counter fields */}
              {!isPriceNotDetermined ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-2 xl:grid-cols-2 sm:grid-cols-1 gap-x-4">
                  <Controller
                    name="priceFrom"
                    control={control}
                    defaultValue={existingOrder?.priceFrom || 0}
                    rules={{ required: !isPriceNotDetermined }}
                    render={({ field }) => (
                      <Counter
                        {...field}
                        initialValue={existingOrder?.priceFrom}
                        icon={userMail}
                        label={t('myOrders.price')}
                        placeholder={t('myOrders.from')}
                        className="mt-2"
                        step={10000}
                      />
                    )}
                  />
                  <Controller
                    name="priceTo"
                    control={control}
                    defaultValue={existingOrder?.priceTo || 0}
                    render={({ field }) => (
                      <Counter
                        {...field}
                        initialValue={existingOrder?.priceTo}
                        icon={''}
                        label={t('myOrders.to')}
                        placeholder={t('myOrders.to')}
                        className="mt-2"
                        step={10000}
                      />
                    )}
                  />
                  <div className="mb-4">
                    {errors.priceFrom && (
                      <span className="text-red-500 text-sm block mt-1">
                        {errors.priceFrom.message}
                      </span>
                    )}
                  </div>
                  <div className="mb-4">
                    {errors.priceTo && (
                      <span className="text-red-500 text-sm block mt-1">
                        {errors.priceTo.message}
                      </span>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
            <div className="flex flex-col max600:mt-5 sm:mt-5">
              <Controller
                name="description"
                control={control}
                render={({ field }) => (
                  <div>
                    <Textarea
                      rows={5}
                      label={t('myOrders.description')}
                      placeholder={t('myOrders.description')}
                      maxLength={1000}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        if (e.target.value.length > 2000) {
                          toast.warning(
                            t('addOffer.descriptionTooLong') ||
                              'Description cannot exceed 2000 characters'
                          );
                        }
                      }}
                    />
                    <div className="text-sm text-gray-500 mt-1">
                      {field.value?.length || 0}/2000
                    </div>
                  </div>
                )}
              />
            </div>
          </div>
          {/* <WayConnection
            selectedOptions={selectedCommunicationOptions}
            onSelect={handleCommunicationOptionsSelect}
          /> */}
          ˝
          <div
            className={`w-[50%] mr-auto ml-auto gap-x-2 ${
              isEditMode
                ? 'grid grid-cols-1 justify-center'
                : 'grid md:grid-cols-2 lg:grid-cols-2 2xl:grid-cols-2 xl:grid-cols-2 sm:grid-cols-1'
            }`}
          >
            <Button
              type="submit"
              title={
                isEditMode ? t('myOrders.EditOrder') : t('myOrders.addOrderNow')
              }
              dark={false}
              className="bg-[#00CEBC] border border-[#00CEBC]"
              loading={loading}
            />
            {!isEditMode && (
              <Button
                onClick={() => {
                  reset();
                  navigate('/orders');
                }}
                title={t('myOrders.closeOrder')}
                dark={false}
                className="bg-[#FF4342] border border-[#FF4342] max600:mt-3 sm:mt-3 xl:mt-0 lg:mt-0 2xl:mt-0 md:mt-0"
              />
            )}
          </div>
        </form>

        <MAddOrderConfirmation
          isOpen={openModal}
          Close={closeModal}
          isEditMode={isEditMode}
        />
      </div>
    </div>
  );
};

export default Add;
